import {
  Flex,
  IconButton,
  Input,
  ListIcon,
  ListItem,
  Text,
  Tooltip,
  useBoolean,
  useDisclosure,
  useOutsideClick,
} from '@chakra-ui/react';

import {
  FC,
  KeyboardEventHandler,
  MouseEventHandler,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { IconType } from 'react-icons';
import { shallow } from 'zustand/shallow';
import { useEditorStageView } from '../../contexts/EditorStageViewContext';
import { useStore } from '../../hooks/useStore';
import { LuTrash2, LuEye, LuEyeOff, LuUnlock, LuLock } from 'react-icons/lu';
import DeletePopover from './DeletePopover';
import ElementReorderMenu from './ElementReorderMenu';

const IconTooltip: FC<PropsWithChildren<{ label: string }>> = ({ label, children }) => (
  <Tooltip label={label} placement="top" hasArrow openDelay={400}>
    {children}
  </Tooltip>
);

const ACTION_SLOT_PX = 24;
const ACTION_BAR_PADDING_PX = 4;

// Slots keep their width when empty so the row's icons never change position.
const ActionSlot: FC<PropsWithChildren<{ isVisible: boolean }>> = ({ isVisible, children }) => (
  <Flex
    w={`${ACTION_SLOT_PX}px`}
    h={`${ACTION_SLOT_PX}px`}
    flexShrink={0}
    alignItems="center"
    justifyContent="center"
    opacity={isVisible ? 1 : 0}
    pointerEvents={isVisible ? 'auto' : 'none'}
  >
    {children}
  </Flex>
);

type Props = {
  itemId: string;
  name: string;
  elementIcon: IconType;
};

const ElementItem: FC<PropsWithChildren<Props>> = ({ itemId, name, elementIcon }) => {
  const { isEditorStage } = useEditorStageView();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const listItemRef = useRef<HTMLLIElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [showEdit, setShowEdit] = useBoolean();
  const [isHovering, setIsHovering] = useState(false);
  const [newName, setNewName] = useState(name);

  const s = useStore(
    (state) => ({
      elements: state.elements,
      updateElementName: state.updateElementName,
      updateElementLocked: state.updateElementLocked,
      updateElementHidden: state.updateElementHidden,
      selectedElementIds: state.selectedElementIds,
      setSelectedElement: state.setElementSelected,
      toggleSelectedElement: state.toggleElementSelected,
      deleteElements: state.deleteElements,
    }),
    shallow,
  );

  const element = useMemo(() => {
    const element = s.elements.find((el) => el.id === itemId);
    return element;
  }, [itemId, s.elements]);

  const isLocked = Boolean(element?.locked);
  const isHidden = Boolean(element?.hidden);
  const isSelected = s.selectedElementIds.includes(itemId);
  const [isReorderMenuOpen, setIsReorderMenuOpen] = useState(false);
  const showHoverActions = (isHovering || isSelected || isReorderMenuOpen) && !showEdit;
  const canToggleLockAndHide = isEditorStage;

  const handleClick: MouseEventHandler<HTMLLIElement> = useCallback(
    (evt) => {
      if (evt.shiftKey || evt.metaKey || evt.ctrlKey) {
        s.toggleSelectedElement(itemId);
        return;
      }
      s.setSelectedElement(itemId);
    },
    [itemId, s],
  );

  const handleMouseDown: MouseEventHandler<HTMLLIElement> = useCallback((evt) => {
    if (evt.detail > 1) {
      evt.preventDefault();
    }
    if (evt.target instanceof Element && evt.target.closest('button')) {
      return;
    }
    listItemRef.current?.focus();
  }, []);

  const handleDoubleClick: MouseEventHandler<HTMLLIElement> = useCallback(
    (evt) => {
      if (evt.target instanceof Element && evt.target.closest('button')) {
        return;
      }
      s.setSelectedElement(itemId);
      if (isLocked) {
        return;
      }
      setShowEdit.on();
    },
    [isLocked, s, itemId, setShowEdit],
  );

  const handleOnKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (evt) => {
      evt.stopPropagation();
      if (evt.key !== 'Enter' && evt.key !== 'Escape') {
        return;
      }
      if (evt.key === 'Enter') {
        s.updateElementName(itemId, newName);
      }
      if (evt.key === 'Escape') {
        setNewName(name);
      }
      setShowEdit.off();
    },
    [itemId, name, newName, s, setShowEdit],
  );

  const handleListItemKeyDown: KeyboardEventHandler<HTMLLIElement> = useCallback(
    (evt) => {
      if (isLocked) {
        return;
      }
      if (
        (evt.key === 'Backspace' || evt.key === 'Delete') &&
        !evt.metaKey &&
        !evt.ctrlKey &&
        !evt.altKey &&
        s.selectedElementIds.includes(itemId)
      ) {
        // The frame itself can be selected here, but only the trash can removes it.
        const deletable = s.selectedElementIds.filter((id) =>
          s.elements.some((el) => el.id === id && el.type !== 'SVG'),
        );
        if (deletable.length === 0) {
          return;
        }
        evt.preventDefault();
        // The canvas listens on window for the same key: handle it once, here.
        evt.stopPropagation();
        // No confirmation: undo brings the elements back.
        s.deleteElements(deletable);
      }
    },
    [isLocked, itemId, s],
  );

  const handleDelete = useCallback(() => {
    s.deleteElements([itemId]);
  }, [itemId, s]);

  const handleToggleLocked: MouseEventHandler<HTMLButtonElement> = useCallback(
    (evt) => {
      evt.stopPropagation();
      s.updateElementLocked(itemId, !isLocked);
    },
    [isLocked, itemId, s],
  );

  const handleToggleHidden: MouseEventHandler<HTMLButtonElement> = useCallback(
    (evt) => {
      evt.stopPropagation();
      s.updateElementHidden(itemId, !isHidden);
    },
    [isHidden, itemId, s],
  );

  useOutsideClick({
    ref: inputRef,
    handler: () => {
      setNewName(name);
      setShowEdit.off();
    },
  });

  useEffect(() => {
    if (isLocked && showEdit) {
      setNewName(name);
      setShowEdit.off();
    }
  }, [isLocked, name, setShowEdit, showEdit]);

  useEffect(() => {
    if (showEdit && inputRef.current) {
      inputRef.current.selectionStart = 0;
      inputRef.current.selectionEnd = -1;
      inputRef.current.focus();
      // Return focus to the list item when the user
      // has finished editing the element's name
    } else if (!showEdit && listItemRef.current) {
      listItemRef.current.focus();
    }
  }, [showEdit]);

  const hasVisibilitySlot = canToggleLockAndHide || isHidden;
  const hasLockSlot = canToggleLockAndHide || isLocked;

  // Persistent status icons stay visible without hover, so the name must stop before them.
  const persistentSlotCount = isHidden ? (hasLockSlot ? 3 : 2) : isLocked ? 2 : 0;
  const persistentActionsWidth =
    persistentSlotCount > 0
      ? `${persistentSlotCount * ACTION_SLOT_PX + ACTION_BAR_PADDING_PX}px`
      : undefined;

  const actionBarBackground = isSelected ? 'blue.50' : 'white';

  return (
    <DeletePopover isOpen={isOpen} onClose={onClose} onDelete={handleDelete}>
      <ListItem
        ref={listItemRef}
        pl={2}
        py={0}
        height={8}
        key={itemId}
        display={'flex'}
        alignItems={'center'}
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        userSelect="none"
        _hover={{
          outlineWidth: 1,
          outlineColor: 'blue.100',
          outlineStyle: 'solid',
          outlineOffset: 'inset',
        }}
        data-test-id={`element-${element?.type}`}
        data-element-id={itemId}
        color={isSelected ? 'blue.600' : 'inherit'}
        bg={isSelected ? 'blue.50' : 'inherit'}
        cursor="pointer"
        onDoubleClick={handleDoubleClick}
        onKeyDown={handleListItemKeyDown}
        onMouseOver={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        tabIndex={-1}
      >
        <Flex alignItems={'center'} width="100%" minW={0} position="relative">
          <ListIcon as={elementIcon} mr="0.5" flexShrink={0} opacity={isHidden ? 0.5 : 1} />
          {showEdit && !isLocked ? (
            <Input
              ref={inputRef}
              marginLeft="-1px"
              marginRight={persistentActionsWidth ?? '1px'}
              size="xs"
              fontSize="sm"
              value={newName}
              onKeyDown={handleOnKeyDown}
              onChange={(event) => setNewName(event.target.value)}
              onClick={(evt) => {
                evt.stopPropagation();
              }}
            />
          ) : (
            <Text
              cursor={isLocked ? 'default' : 'pointer'}
              userSelect="none"
              opacity={isHidden ? 0.5 : 1}
              flex={1}
              minW={0}
              pl="2"
              pr={persistentActionsWidth}
              as="span"
              fontSize="sm"
              overflow="hidden"
              whiteSpace="nowrap"
              textOverflow="ellipsis"
            >
              {name}
            </Text>
          )}
          <Flex
            position="absolute"
            right={0}
            top={0}
            bottom={0}
            alignItems="center"
            pl={2}
            pr={`${ACTION_BAR_PADDING_PX}px`}
            bg={showHoverActions ? actionBarBackground : 'transparent'}
            pointerEvents={showHoverActions ? 'auto' : 'none'}
            zIndex={1}
            _before={
              showHoverActions
                ? {
                    content: '""',
                    position: 'absolute',
                    left: '-12px',
                    top: 0,
                    bottom: 0,
                    width: '12px',
                    bgGradient: `linear(to-r, transparent, ${actionBarBackground})`,
                    pointerEvents: 'none',
                  }
                : undefined
            }
          >
            <Flex opacity={showHoverActions ? 1 : 0}>
              <ElementReorderMenu itemId={itemId} onOpenChange={setIsReorderMenuOpen} />
            </Flex>
            {hasVisibilitySlot && (
              <ActionSlot isVisible={showHoverActions || isHidden}>
                <IconTooltip label="Visibility">
                  {canToggleLockAndHide ? (
                    <IconButton
                      size="xs"
                      icon={isHidden ? <LuEyeOff size="14" /> : <LuEye size="14" />}
                      aria-label={isHidden ? 'Show element' : 'Hide element'}
                      onClick={handleToggleHidden}
                      colorScheme={isHidden ? 'blue' : 'gray'}
                      variant="ghost"
                      rounded="full"
                    />
                  ) : (
                    <Flex color="blue.500" aria-label="Hidden element" alignItems="center">
                      <LuEyeOff size="14" />
                    </Flex>
                  )}
                </IconTooltip>
              </ActionSlot>
            )}
            {hasLockSlot && (
              <ActionSlot isVisible={showHoverActions || isLocked}>
                <IconTooltip label="Locking">
                  {canToggleLockAndHide ? (
                    <IconButton
                      size="xs"
                      icon={isLocked ? <LuLock size="14" /> : <LuUnlock size="14" />}
                      aria-label={isLocked ? 'Unlock element' : 'Lock element'}
                      onClick={handleToggleLocked}
                      colorScheme={isLocked ? 'blue' : 'gray'}
                      variant="ghost"
                      rounded="full"
                    />
                  ) : (
                    <Flex color="blue.500" aria-label="Locked element" alignItems="center">
                      <LuLock size="14" />
                    </Flex>
                  )}
                </IconTooltip>
              </ActionSlot>
            )}
            <ActionSlot isVisible={showHoverActions && !isLocked}>
              {!isLocked && (
                <IconTooltip label="Remove">
                  <IconButton
                    size="xs"
                    icon={<LuTrash2 size="14" />}
                    aria-label="trash-icon"
                    onClick={onOpen}
                    colorScheme="red"
                    variant="ghost"
                    rounded="full"
                  />
                </IconTooltip>
              )}
            </ActionSlot>
          </Flex>
        </Flex>
      </ListItem>
    </DeletePopover>
  );
};

ElementItem.displayName = 'ElementItem';

export default ElementItem;
